import React from 'react';
import { Facebook, Instagram, Twitter, Youtube, ArrowUpRight } from 'lucide-react';
import { contactInfo, navLinks } from '../mock';

const FOOTER_LOGO = '/logo-ionic-horizontal.png';

const TikTokIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M20 8.5a6.5 6.5 0 0 1-4-1.35V15a5 5 0 1 1-5-5v3.2a1.8 1.8 0 1 0 1.8 1.8V2h3a4.5 4.5 0 0 0 4.2 4.2V8.5Z" />
  </svg>
);

const iconFor = (name) => {
  if (name === 'Facebook') return <Facebook className="w-3.5 h-3.5" />;
  if (name === 'Instagram') return <Instagram className="w-3.5 h-3.5" />;
  if (name === 'X') return <Twitter className="w-3.5 h-3.5" />;
  if (name === 'TikTok') return <TikTokIcon className="w-3.5 h-3.5" />;
  if (name === 'YouTube') return <Youtube className="w-3.5 h-3.5" />;
  return <ArrowUpRight className="w-3.5 h-3.5" />;
};

const FooterLogo = ({ src, wrapped = true, alt = 'Ionic Estate Sales' }) =>
  wrapped ? (
    <div className="inline-flex items-center bg-white rounded-md p-2 shadow-sm">
      <img
        src={src}
        alt={alt}
        className="h-14 md:h-16 w-auto object-contain select-none"
        draggable={false}
      />
    </div>
  ) : (
    <img
      src={src}
      alt={alt}
      className="h-16 md:h-20 w-auto object-contain select-none"
      draggable={false}
    />
  );

const Footer = ({ logoSrc = FOOTER_LOGO, logoWrapped = true, logoAlt = 'Ionic Estate Sales' } = {}) => {
  return (
    <footer className="relative pt-16 pb-10 px-6 md:px-10 border-t border-white/10 overflow-hidden">
      <div className="relative max-w-[1500px] mx-auto">
        <div className="grid lg:grid-cols-[1.5fr_1fr_1fr_1fr] gap-10">
          <div>
            <FooterLogo src={logoSrc} wrapped={logoWrapped} alt={logoAlt} />
            <p className="mt-6 text-[color:var(--parchment)]/70 leading-relaxed max-w-sm">
              A full-service estate liquidation company with more than 30 years of industry experience. Professional, transparent, and committed to maximizing value for every family we serve.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {contactInfo.socials.map((s) => (
                <a
                  key={s.name}
                  href={s.href}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/15 text-[12px] md:text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[color:var(--parchment)] hover:border-[color:var(--green)] hover:text-[color:var(--green)] transition-colors"
                >
                  {iconFor(s.name)} {s.name}
                </a>
              ))}
            </div>
          </div>

          <div>
            <div className="font-mono text-[12px] md:text-[10.5px] tracking-[0.24em] uppercase text-[color:var(--green)] mb-5">Navigate</div>
            <ul className="space-y-3">
              {navLinks.map((l) => (
                <li key={l.label}>
                  <a href={l.href} className="link-under text-[color:var(--parchment)]/85 hover:text-white text-sm font-semibold uppercase tracking-[0.08em]">{l.label}</a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <div className="font-mono text-[12px] md:text-[10.5px] tracking-[0.24em] uppercase text-[color:var(--green)] mb-5">What We Do</div>
            <ul className="space-y-3">
              <li className="text-[color:var(--parchment)]/85 text-sm font-semibold uppercase tracking-[0.08em]">Estate Sales</li>
              <li className="text-[color:var(--parchment)]/85 text-sm font-semibold uppercase tracking-[0.08em]">Downsizing</li>
              <li className="text-[color:var(--parchment)]/85 text-sm font-semibold uppercase tracking-[0.08em]">Business Liquidation</li>
              <li className="text-[color:var(--parchment)]/85 text-sm font-semibold uppercase tracking-[0.08em]">Staging &amp; Marketing</li>
              <li className="text-[color:var(--parchment)]/85 text-sm font-semibold uppercase tracking-[0.08em]">Cleanout &amp; Close-Out</li>
            </ul>
          </div>

          <div>
            <div className="font-mono text-[12px] md:text-[10.5px] tracking-[0.24em] uppercase text-[color:var(--green)] mb-5">Contact</div>
            <a href={contactInfo.phoneHref} className="block font-display text-2xl text-[color:var(--parchment)] hover:text-[color:var(--green)] transition-colors">{contactInfo.phone}</a>
            <a href={contactInfo.emailHref} data-testid="footer-email-link" className="mt-2 block text-[color:var(--parchment)]/70 hover:text-[color:var(--green)] text-sm transition-colors">{contactInfo.emailDisplay}</a>
            <div className="mt-4 text-[color:var(--parchment)]/70 text-sm leading-relaxed">Southern Oregon &middot; Portland &middot; Vancouver</div>
            <a href="#sell" className="mt-6 inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] border-b border-[color:var(--green)] pb-1 text-[color:var(--parchment)] hover:text-[color:var(--green)] transition-colors">
              Need our help <ArrowUpRight className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>

        <div className="mt-16 border-t border-white/10 pt-10">
          <img
            src="/ionic-wordmark.png"
            alt="IONIC"
            className="ionic-wordmark w-full h-auto select-none"
            draggable={false}
          />
        </div>

        <div className="mt-8 flex flex-col md:flex-row justify-between items-center gap-3 font-mono text-[12px] md:text-[10.5px] tracking-[0.22em] uppercase text-[color:var(--parchment)]/50">
          <div>&copy; {new Date().getFullYear()} Ionic Estate Sales &mdash; All Rights Reserved</div>
          <div className="flex items-center gap-5 md:gap-6">
            <a href="#" className="link-under inline-block py-2 min-h-[44px] md:min-h-0 md:py-0 flex items-center">Privacy</a>
            <a href="#" className="link-under inline-block py-2 min-h-[44px] md:min-h-0 md:py-0 flex items-center">Terms</a>
            <a href="#" className="link-under inline-block py-2 min-h-[44px] md:min-h-0 md:py-0 flex items-center">Accessibility</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
